import React, { useState, useEffect, useRef } from 'react';
import { MessageCircle, X } from 'lucide-react';
import AIChat from './AIChat';
import { useIsMobile } from '@/hooks/useIsMobile';

const DraggableChatButton = () => {
  const isMobile = useIsMobile();
  const [chatOpen, setChatOpen] = useState(false);
  const [isHidden, setIsHidden] = useState(() => localStorage.getItem('chatbot_hidden') === 'true');
  
  // Posición Y por defecto: sobre la barra de navegación
  const defaultY = window.innerHeight - 140;
  
  // Posición inicial: pegado a la orilla derecha
  const initialX = window.innerWidth - 58;

  const [position, setPosition] = useState({ x: initialX, y: defaultY });
  const [side, setSide] = useState('right');
  const [isDraggingState, setIsDraggingState] = useState(false);
  const [isOverDropZone, setIsOverDropZone] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isDragging = useRef(false);
  const startPos = useRef({ x: 0, y: 0 });
  const initialPos = useRef({ x: 0, y: 0 });
  const buttonRef = useRef(null);
  const posRef = useRef({ x: initialX, y: defaultY });
  const isOverRef = useRef(false);

  useEffect(() => { posRef.current = position; }, [position]);

  // Recalcular posición al cambiar tamaño de pantalla o modo
  useEffect(() => {
    const handleResize = () => {
      const restX = side === 'left' ? 2 : window.innerWidth - 58;
      setPosition(prev => ({ x: restX, y: prev.y }));
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [side]);

  // Reactivar chatbot desde el menú del perfil/ajustes
  useEffect(() => {
    const handleReactivate = () => {
      setIsHidden(false);
      localStorage.setItem('chatbot_hidden', 'false');
      window.dispatchEvent(new Event('chatbot-hidden-changed'));
      const restX = window.innerWidth - 58;
      const p = { x: restX, y: window.innerHeight - 140 };
      setPosition(p);
      posRef.current = p;
      setSide('right');
    };
    window.addEventListener('reactivate-chatbot', handleReactivate);
    return () => window.removeEventListener('reactivate-chatbot', handleReactivate);
  }, []);

  // Event listener touchmove: se vuelve a ligar SIEMPRE que cambie chatOpen o isHidden
  useEffect(() => {
    if (!isMobile || chatOpen || isHidden) return;

    const el = buttonRef.current;
    if (!el) return;

    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!isDragging.current) {
        if (Math.abs(t.clientX - startPos.current.x) > 3 || Math.abs(t.clientY - startPos.current.y) > 3) {
          isDragging.current = true;
          setIsDraggingState(true);
        } else return;
      }
      e.preventDefault();
      const newX = Math.max(-20, Math.min(window.innerWidth - 36, initialPos.current.x + t.clientX - startPos.current.x));
      const newY = Math.max(60, Math.min(window.innerHeight - 110, initialPos.current.y + t.clientY - startPos.current.y));
      
      setPosition({ x: newX, y: newY });
      posRef.current = { x: newX, y: newY };

      const dist = Math.hypot(newX - (window.innerWidth / 2 - 28), newY - (window.innerHeight - 90));
      const over = dist < 70;
      isOverRef.current = over;
      setIsOverDropZone(over);
    };

    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [isMobile, chatOpen, isHidden]);

  const handleTouchStart = (e) => {
    if (!isMobile) return;
    isDragging.current = false;
    startPos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    initialPos.current = { ...posRef.current };
  };

  const getRestingX = (s) => {
    return s === 'left' ? 2 : window.innerWidth - 58;
  };

  const snapEnd = () => {
    if (isMobile && isOverRef.current) {
      setIsHidden(true);
      localStorage.setItem('chatbot_hidden', 'true');
      window.dispatchEvent(new Event('chatbot-hidden-changed'));
    } else {
      const snapLeft = posRef.current.x < window.innerWidth / 2;
      const targetSide = snapLeft ? 'left' : 'right';
      setSide(targetSide);
      const targetX = getRestingX(targetSide);
      setPosition(prev => ({ x: targetX, y: prev.y }));
    }
    isDragging.current = false;
    setIsDraggingState(false);
    setIsOverDropZone(false);
    isOverRef.current = false;
  };

  const handleTouchEnd = () => {
    if (!isMobile || !isDragging.current) {
      setChatOpen(true);
    } else {
      snapEnd();
    }
  };

  const handleMouseEnter = () => {
    if (isMobile) return;
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (isMobile) return;
    setIsHovered(false);
  };

  if (isHidden) return null;

  // Si el modal del chat está abierto, mostrar ÚNICAMENTE el chat y ocultar la burbuja
  if (chatOpen) {
    return <AIChat isOpen={true} onClose={() => setChatOpen(false)} />;
  }

  return (
    <>
      {/* Zona X para descartar (Solo Móvil) */}
      {isMobile && isDraggingState && (
        <div
          style={{
            width: isOverDropZone ? '4.5rem' : '3.5rem',
            height: isOverDropZone ? '4.5rem' : '3.5rem',
            zIndex: 9998,
          }}
          className={`fixed bottom-10 left-1/2 -translate-x-1/2 pointer-events-none flex items-center justify-center rounded-full border-2 border-white shadow-2xl transition-all duration-200 ${isOverDropZone ? 'bg-rose-700' : 'bg-rose-600/90'}`}
        >
          <X className="w-7 h-7 text-white" />
        </div>
      )}

      {/* Burbuja Flotante Esférica Completa */}
      <button
        ref={buttonRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setChatOpen(true)}
        style={{
          transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${isOverDropZone ? 0.75 : 1})`,
          transition: isDraggingState
            ? (isOverDropZone ? 'transform 0.2s ease, opacity 0.2s ease' : 'none')
            : 'transform 0.3s cubic-bezier(0.2,0.8,0.2,1)',
          opacity: isOverDropZone ? 0.7 : 1,
          zIndex: 9999,
        }}
        className="fixed top-0 left-0 text-white shadow-2xl active:scale-95 touch-none select-none flex items-center justify-center w-14 h-14 rounded-full cursor-pointer group"
        aria-label="Asistente IA"
      >
        {/* Glow perimetral pulsante circular */}
        <span className="absolute inset-0 rounded-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 opacity-70 blur-md group-hover:opacity-100 transition-opacity" />

        {/* Cuerpo principal esférico con gradiente y efecto cristal */}
        <div className="relative w-full h-full rounded-full bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 border border-white/40 backdrop-blur-md flex items-center justify-center shadow-lg overflow-hidden">
          {/* Brillo de luz superior esférico */}
          <div className="absolute top-0 inset-x-0 h-1/2 bg-gradient-to-b from-white/30 to-transparent rounded-t-full pointer-events-none" />

          {/* Ícono de Asistente IA */}
          <div className="relative flex items-center justify-center">
            <MessageCircle className="w-6 h-6 text-white drop-shadow-md" />
            <span className="absolute -top-1 -right-1 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-300"></span>
            </span>
          </div>
        </div>
      </button>

      <AIChat isOpen={false} onClose={() => setChatOpen(false)} />
    </>
  );
};

export default DraggableChatButton;
